/**
 * Storage service abstraction layer
 * Uses Supabase for both authenticated and guest users
 * Guest users use a shared guest user ID for analytics
 */

import { supabase } from '@/lib/supabase'
import { isGuestMode } from '@/utils/iframeDetector'
import { User } from '@supabase/supabase-js'

// Single guest user ID for all guest mode usage (UUID from auth.users)
const GUEST_USER_ID = '00000000-0000-0000-0000-000000000000'

export interface PromptData {
  id: string
  user_id?: string // Optional for guest mode
  original_prompt: string
  context: {
    questions: string[]
    user_input: string
    context_output: string
  }
  role: {
    generated_roles: string[]
    selected_role: string
  }
  action: string
  format: any
  tone_scores: any
  additional_tones: string[]
  example: string
  final_prompt: string
  created_at: string
  updated_at: string
}


class StorageService {
  private isGuest(): boolean {
    return isGuestMode()
  }

  // Get the user ID to use (guest ID for guest mode, actual user ID for authenticated)
  private getUserId(user: User | null): string {
    if (this.isGuest()) {
      return GUEST_USER_ID
    }
    if (!user) {
      throw new Error('User required for authenticated mode')
    }
    return user.id
  }

  // Create a new prompt
  async createPrompt(data: Omit<PromptData, 'id' | 'created_at' | 'updated_at'>, user: User | null): Promise<PromptData> {
    const userId = this.getUserId(user)
    
    const { data: result, error } = await supabase
      .from('prompts')
      .insert({
        ...data,
        user_id: userId,
      })
      .select()
      .single()

    if (error) throw error
    return result as PromptData
  }

  // Load a prompt by ID
  async loadPrompt(id: string, user: User | null): Promise<PromptData | null> {
    const userId = this.getUserId(user)
    
    const { data, error } = await supabase
      .from('prompts')
      .select('*')
      .eq('id', id)
      .eq('user_id', userId)
      .single()

    if (error) {
      console.error('Error loading prompt:', error)
      return null
    }
    return data as PromptData
  }

  // Update a prompt
  async updatePrompt(id: string, updates: Partial<PromptData>, user: User | null): Promise<void> {
    const userId = this.getUserId(user)
    
    const { error } = await supabase
      .from('prompts')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)

    if (error) throw error
  }

  // Get all prompts for a user
  async getAllPrompts(user: User | null): Promise<PromptData[]> {
    const userId = this.getUserId(user)
    
    const { data, error } = await supabase
      .from('prompts')
      .select('id, original_prompt, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error loading prompts:', error)
      return []
    }
    return data as PromptData[]
  }
}

export const storageService = new StorageService()

